import { useEffect, useRef, useState, type FormEvent } from 'react';

import { LANG_CODES, type Concept } from '@lexio/shared';

import { ConceptEditor } from '../components/ConceptEditor';
import { ApiError, captureConcept, translate } from '../lib/api';
import { ArticleExtract } from './ArticleExtract';

/** Pod touto jistotou MyMemory varujeme, ze preklad muze byt nesmysl. */
const UNSURE_MATCH = 0.5;

type Pair = 'cs' | 'en';
type Mode = 'word' | 'article';

function flip(lang: Pair): Pair {
  return lang === 'cs' ? 'en' : 'cs';
}

function queryFromHash(): string {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const qi = raw.indexOf('?');
  if (qi < 0) return '';
  return new URLSearchParams(raw.slice(qi + 1)).get('q')?.trim() ?? '';
}

/**
 * Mobilni slovnicek: preklad EN↔CS a ulozeni do balicku „Moje zachycené“,
 * odkud to jde zkouset stejne jako ostatni koncepty.
 */
export function Translate() {
  const [mode, setMode] = useState<Mode>('word');
  const [from, setFrom] = useState<Pair>('en');
  const [text, setText] = useState(() => queryFromHash());
  const [translated, setTranslated] = useState('');
  const [to, setTo] = useState<Pair>('cs');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [match, setMatch] = useState(1);
  const [alternatives, setAlternatives] = useState<string[]>([]);
  const [note, setNote] = useState('');
  /** Koncepty, ktere uz vyraz maji - ukazeme je, at se nepridava duplicita. */
  const [existing, setExisting] = useState<Concept[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const resetResult = () => {
    setTranslated('');
    setMatch(1);
    setAlternatives([]);
    setNote('');
    setExisting([]);
    setEditingId(null);
    setError(null);
    setInfo(null);
  };

  /** Krizek v poli: smazat vse a rovnou psat dalsi slovo. */
  const clear = () => {
    setText('');
    resetResult();
    inputRef.current?.focus();
  };

  const runTranslate = async (e?: FormEvent, override?: string) => {
    e?.preventDefault();
    const q = (override ?? text).trim();
    if (!q) return;
    setBusy(true);
    resetResult();
    try {
      const res = await translate(q, from, to);
      setTranslated(res.translated);
      setMatch(res.match);
      setAlternatives(res.alternatives);
      setNote(res.note);
      setExisting(res.existing);
      setFrom(res.from);
      setTo(res.to);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Překlad se nepodařil');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const apply = () => {
      const q = queryFromHash();
      if (!q) return;
      setMode('word');
      setText(q);
      void runTranslate(undefined, q);
    };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
    // Prvni nacteni + text z Chrome panelu (#/translate?q=…).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const swap = () => {
    setFrom(flip(from));
    setTo(flip(to));
    const prevText = text;
    resetResult();
    setText(translated);
    setTranslated(prevText);
  };

  /** Po ulozeni nebo uprave koncept zobrazime mezi "uz mas ve slovniku". */
  const upsertExisting = (c: Concept) =>
    setExisting((prev) => [c, ...prev.filter((x) => x.id !== c.id)]);

  const save = async () => {
    const source = text.trim();
    const target = translated.trim();
    if (!source || !target) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const res = await captureConcept({
        sourceLang: from,
        targetLang: to,
        sourceTerm: source,
        targetTerm: target,
        // Nevybrane navrhy se hodi jako dalsi vyznamy, poznamka z AI jako vysvetleni.
        targetVariants: alternatives.filter((a) => a.trim() && a.trim() !== target),
        targetNote: note || undefined,
      });
      upsertExisting(res.concept);
      setInfo(
        res.alreadyExisted
          ? 'Už ve slovníku je — při dalším učení ho uvidíš dřív. Upravit ho můžeš níž.'
          : 'Přidáno ke zkoušení (balíček Moje zachycené). Upravit ho můžeš níž.',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Uložení se nepodařilo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="translate">
      <h1 className="page-title">Překlad</h1>
      <div className="row translate-dir">
        <button
          type="button"
          className={`btn ghost small ${mode === 'word' ? 'active' : ''}`}
          onClick={() => setMode('word')}
        >
          Slovo
        </button>
        <button
          type="button"
          className={`btn ghost small ${mode === 'article' ? 'active' : ''}`}
          onClick={() => setMode('article')}
        >
          Článek
        </button>
      </div>

      {mode === 'article' ? (
        <ArticleExtract />
      ) : (
        <>
      <p className="muted small">Angličtina ↔ čeština. Co potkáš, přelož a hoď ke zkoušení.</p>

      <div className="row translate-dir">
        <button type="button" className={`btn ghost small ${from === 'en' ? 'active' : ''}`} onClick={() => { setFrom('en'); setTo('cs'); }}>
          EN → CS
        </button>
        <button type="button" className={`btn ghost small ${from === 'cs' ? 'active' : ''}`} onClick={() => { setFrom('cs'); setTo('en'); }}>
          CS → EN
        </button>
        <button type="button" className="btn ghost small" onClick={swap} aria-label="Prohodit směr">
          ⇄
        </button>
      </div>

      <form onSubmit={runTranslate} className="translate-form">
        <div className="translate-input">
          <textarea
            ref={inputRef}
            className="input translate-box"
            rows={3}
            placeholder={from === 'en' ? 'Word or short phrase…' : 'Slovo nebo krátká fráze…'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              // Ctrl+Enter = novy radek, samotny Enter = Prelozit.
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const el = e.currentTarget;
                const at = el.selectionStart;
                const next = `${text.slice(0, at)}\n${text.slice(el.selectionEnd)}`;
                setText(next);
                requestAnimationFrame(() => el.setSelectionRange(at + 1, at + 1));
                return;
              }
              if (!e.shiftKey) void runTranslate(e);
            }}
            autoCapitalize="off"
            autoCorrect="off"
          />
          {text && (
            <button type="button" className="clear-btn" onClick={clear} aria-label="Smazat a zadat další">
              ✕
            </button>
          )}
        </div>
        <button className="btn primary" type="submit" disabled={busy || !text.trim()}>
          {busy ? 'Překládám…' : 'Přeložit'}
        </button>
      </form>

      {translated && (
        <div className="translate-result">
          {/* Preklad jde pred pridanim opravit - MyMemory obcas vrati nesmysl. */}
          <input
            className="input translate-out"
            value={translated}
            onChange={(e) => setTranslated(e.target.value)}
            aria-label="Překlad (lze upravit)"
          />
          {match < UNSURE_MATCH && (
            <p className="warn small">
              ⚠ Nejistý překlad – překladač to slovo asi nezná (překlep?). Zkontroluj ho, případně ho přepiš.
            </p>
          )}
          {note && <p className="muted small">💡 {note}</p>}
          {alternatives.length > 0 && (
            <div className="chips wrap">
              {alternatives.map((a) => (
                <button key={a} type="button" className="chip-btn" onClick={() => setTranslated(a)}>
                  {a}
                </button>
              ))}
            </div>
          )}
          <button className="btn primary" type="button" onClick={() => void save()} disabled={saving || !translated.trim()}>
            {saving ? 'Ukládám…' : 'Přidat ke zkoušení'}
          </button>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {info && <p className="muted">{info}</p>}

      {existing.length > 0 && (
        <div className="translate-existing">
          <div className="lang-label">Už máš ve slovníku</div>
          {existing.map((c) =>
            editingId === c.id ? (
              <ConceptEditor
                key={c.id}
                concept={c}
                onCancel={() => setEditingId(null)}
                onSaved={(saved) => {
                  upsertExisting(saved);
                  setEditingId(null);
                }}
              />
            ) : (
              <div className="existing-item" key={c.id}>
                <div>
                  <div className="entry-term">{c.terms[from]?.term ?? c.key}</div>
                  <div className="entry-trans">
                    {c.terms[to]?.term ?? <span className="muted">bez překladu ({LANG_CODES[to]})</span>}
                    {c.terms[to] && c.terms[to].variants.length > 0 && (
                      <span className="muted small"> · {c.terms[to].variants.join(' · ')}</span>
                    )}
                  </div>
                  {c.terms[to]?.note && <div className="muted small">💡 {c.terms[to].note}</div>}
                </div>
                <button type="button" className="btn ghost small" onClick={() => setEditingId(c.id)}>
                  ✎ Upravit
                </button>
              </div>
            ),
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}
